/* eslint-disable react-hooks-ext/exhaustive-deps -- TODO(react-hooks): 1 hook(s) need dependency review; fix each and remove this directive */
import { useTranslation, type TranslatedString } from "@i18n";
import { renderUserTag } from "@CubeComponents/html/userTagDom";
import { useRef, useState, useEffect, forwardRef } from "react";
import { useParams } from "react-router-dom";
import { useSelector } from "react-redux";
import { useAppDispatch as useDispatch } from "@Reducers/hooks";
import { convertDate } from "@Services/luxonHelper";
import ToolTipComponent from "@CubeComponents/html/tooltip";
import ButtonComponent from "@CubeComponents/form/ButtonComponent";
import ModalStateDialogComponent from "@CubeComponents/html/modal/ModalWithState";
import { BUTTON_TYPES } from "@CubeComponents/constants/globalVariable";
import { showToastNotification } from "@Reducers/toastSlice";
import { TOAST_TYPE_ERROR, TOAST_TYPE_SUCCESS } from "@Services/constants";
import IconComponent from "@CubeComponents/icons";
import { WBSServicesUrl } from "@Services/dataManager";
import { WBSController } from "@Actions/projectSetting";
import _ from "lodash";
import NumericTextBoxComponent from "@CubeComponents/Mui_Form/NumericTextBoxComponent";
import Forbidden from "@Pages/Error/Forbidden";
import { portalCreateRoot as createRoot } from "@CubeComponents/html/cellPortals";
import { Formatters } from "slickgrid-react";
import TextFieldComponent from "@CubeComponents/Mui_Form/TextFieldComponent";
import { RootState } from "@Reducers/index";
import SlickGrid from "@CubeComponents/html/SlickGrid1";
import GridPageLayout from "@CubeComponents/html/GridPageLayout";
import type { ObjectIdString } from "@Helpers/schemas/common.schema";
import type {
  ColumnDefinition,
  GridRowBase,
  SlickGrid1Ref,
  ValidatedColumnDefinition,
} from "@CubeComponents/html/SlickGrid1";

/**
 * A row of the WBS tree grid (`projects/WBSController` list, or the contract/form variants selected by
 * `configParams.dataSourceUrl`). Declared here rather than derived: `wbsControllerResponse.result` is
 * untyped, and the contract-linking columns below only exist on the contract variant.
 */
interface WBSRow extends GridRowBase {
  _id?: ObjectIdString;
  code?: string;
  description?: string;
  unitId?: string;
  type?: string;
  typeId?: string | null;
  heldBy?: string | null;
  reportsTo?: string | null;
  parentId?: ObjectIdString | null;
  treeLevel?: number;
  hasChild?: boolean;
  allParents?: ObjectIdString[];
  createdDate?: number;
  addedOn?: number;
  createdBy?: { _id?: ObjectIdString; fullName?: string };
  addedBy?: { _id?: ObjectIdString; fullName?: string };
  fullName?: string;
  __used?: boolean;
  // Contract/form-linking projection (present only on those data sources).
  originalItemId?: ObjectIdString;
  availableAmount?: number;
  revisedAmount?: number;
  boqItemsAmount?: number;
  linkedAmount?: number;
  isAlreadyAddedInThisContract?: boolean;
  isAlreadyAddedInThisForm?: boolean;
}

/** The row being added/edited inline, plus the two client-only fields that flow tracks. */
type EditingWBSRow = WBSRow & { action?: string; position?: number };

/** The `{ data: { result } }` envelope the WBS remote data sources return. */
type WBSRemoteResponse = { data?: { result?: WBSRow[] } };

/** The per-item amount/percent pair the contract-linking columns edit. */
type WBSCustomValue = { amount?: number; percent?: number };

type ProjectWBSProps = {
  linkWBSButtonStateEnable?: (enabled: boolean) => void;
  showOnlyGrid?: boolean;
  configParams?: {
    dataSourceQuery?: Record<string, unknown>;
    dataSourceUrl?: string;
    idMapping?: string;
    showColumnForParentWbs?: boolean;
    renameHeaders?: boolean;
    showBoqBudgetColumn?: boolean;
    showAvailableBudgetColumn?: boolean;
  };
  contractId?: ObjectIdString;
  /**
   * Only supplied by the contract-linking callers, which are the only ones that render the
   * amount/percent columns — hence optional here and asserted at the two call sites below.
   */
  setCustomValues?: React.Dispatch<React.SetStateAction<Record<string, WBSCustomValue>>>;
  customValues?: Record<string, WBSCustomValue>;
  selectedWBSItems?: ObjectIdString[];
};

interface ToolBarBtnState {
  delete: boolean;
  edit: boolean;
  add_top: boolean;
  add_child: boolean;
  update: boolean;
  cancel: boolean;
  loading: { update: boolean; cancel: boolean };
}

const ProjectWBS = forwardRef(
  (
    {
      linkWBSButtonStateEnable,
      showOnlyGrid = false,
      configParams = {},
      contractId,
      setCustomValues,
      customValues,
      selectedWBSItems = [],
    }: ProjectWBSProps,
    ref: React.ForwardedRef<SlickGrid1Ref<WBSRow>>,
  ) => {
    const { t } = useTranslation();
    const [showDeleteConfirmationModal, setDeleteConfirmationModal] = useState(false);
    const dispatch = useDispatch();
    const [disabledRows, setDisabledRows] = useState(selectedWBSItems);
    // Route params enter from outside the type system; the backend route schema validates this id.
    const params = useParams<{ projectId: ObjectIdString }>();
    const getProjectSettingObject = useSelector((state: RootState) => state.getProjectSettingObject);
    const canAddWBS = getProjectSettingObject.WBS?.add?.enable ?? false;
    const canEditWBS = getProjectSettingObject.WBS?.edit?.enable ?? false;
    const canDeleteWBS = getProjectSettingObject.WBS?.delete?.enable ?? false;
    const [projectWBSQuery] = useState({
      projectId: params.projectId,
      contractId,
      where: [
        {
          isComplex: false,
          field: "projectId",
          operator: "equal",
          ignoreCase: false,
          value: params.projectId,
        },
      ],
    });

    const {
      dataSourceQuery = projectWBSQuery,
      dataSourceUrl = WBSServicesUrl,
      idMapping = "_id",
      showColumnForParentWbs = false,
      renameHeaders = false,
      showBoqBudgetColumn = false,
      showAvailableBudgetColumn = false,
    } = configParams;
    const [isWrapTextActive, setIsWrapTextActive] = useState(false);
    const [toolBarBtnState, setToolBarBtnState] = useState<ToolBarBtnState>({
      delete: false,
      edit: false,
      add_top: false,
      add_child: false,
      update: false,
      cancel: false,
      loading: {
        update: false,
        cancel: false,
      },
    });
    const [editingRowValue, setEditingRowValue] = useState<EditingWBSRow>({});
    const editModeRef = useRef<EditingWBSRow | null>(null);
    const localRef = useRef<SlickGrid1Ref<WBSRow> | null>(null);
    // Callers always hand this component an object ref (`useRef`), never a callback ref.
    const treeGrid = (ref as { current: SlickGrid1Ref<WBSRow> | null } | null) || localRef;

    useEffect(() => {
      editModeRef.current = editingRowValue;
    }, [editingRowValue]);

    useEffect(() => {
      if (editingRowValue.action) return;
      setToolBarBtnState((prev) => ({
        ...prev,
        add_top: canAddWBS,
      }));
    }, [canAddWBS, editingRowValue.action]);

    useEffect(() => {
      if (!contractId) {
        document.title = t("Project Settings | WBS");
      }
    }, [contractId]);

    const addedByTemplate = (_row: number, _cell: number, _value: unknown, _columnDef: unknown, args: WBSRow) => {
      const _id = args?.createdBy?._id || args.addedBy?._id;
      const container = document.createElement("div");
      if (_id) {
        renderUserTag(container, { id: _id });
      }
      return container;
    };

    const toolbarOptions = [
      ...(showOnlyGrid
        ? []
        : [
            {
              name: t("Add"),
              tooltipText: "Add top of the list",
              prefixIcon: "add",
              id: "add_top",
              disabled: !toolBarBtnState.add_top,
            },
            {
              name: "Add Child",
              tooltipText: "Add child of selected item",
              prefixIcon: "add",
              id: "add_child",
              disabled: !toolBarBtnState.add_child,
            },
            {
              name: t("Edit"),
              tooltipText: "Edit selected item",
              id: "edit",
              prefixIcon: "editProfile",
              disabled: !toolBarBtnState.edit,
            },
            {
              name: t("Delete"),
              tooltipText: "Delete selected",
              prefixIcon: "delete",
              id: "delete",
              disabled: !toolBarBtnState.delete,
            },
            {
              name: t("Update"),
              prefixIcon: "Update",
              id: "update",
              disabled: !toolBarBtnState.update,
              loading: toolBarBtnState.loading.update,
            },
            {
              name: t("Cancel"),
              prefixIcon: "cross",
              id: "cancel",
              disabled: !toolBarBtnState.cancel,
              loading: toolBarBtnState.loading.cancel,
            },
          ]),
      {
        name: t("ExpandAll"),
        prefixIcon: "Expand",
        id: "expandAll",
      },
      {
        name: t("CollapseAll"),
        prefixIcon: "Collapse",
        id: "collapseAll",
      },
    ];

    const rowSelectedHandler = () => {
      const totalSelectedRecord = treeGrid.current!.dataView.getAllSelectedItems();
      const noOfRowsSelected = totalSelectedRecord.length;
      if (linkWBSButtonStateEnable) {
        if (noOfRowsSelected > 0) {
          let btnState = true;
          totalSelectedRecord?.map((item) => {
            if (item?.isAlreadyAddedInThisContract || item?.isAlreadyAddedInThisForm) {
              btnState = false;
            }
          });
          linkWBSButtonStateEnable(btnState);
        } else {
          linkWBSButtonStateEnable(false);
        }
      }
      if (!showOnlyGrid && !editingRowValue.action) {
        setToolBarBtnState((prev) => ({
          ...prev,
          add_top: canAddWBS,
          edit: noOfRowsSelected == 1 && canEditWBS,
          delete: noOfRowsSelected > 0 && canDeleteWBS,
          add_child: noOfRowsSelected == 1 && canAddWBS,
        }));
      }
    };

    const dateTemplate = (_row: number, _cell: number, _value: unknown, _columnDef: unknown, args: WBSRow) => {
      const date = args.createdDate || args.addedOn;
      const container = document.createElement("div");
      const root = createRoot(container);
      if (date) {
        const dateFormat = convertDate(Number(date), "dateForTooltip");
        root.render(
          <div className="w-fit">
            <ToolTipComponent showTipPointer position="bottom" content={dateFormat}>
              <span>{convertDate(Number(date), "dateForText")}</span>
            </ToolTipComponent>
          </div>,
        );
      }
      return container;
    };

    const cancelEditRecord = (removeRecord = false) => {
      if (removeRecord) {
        treeGrid.current!.removeRecord!(editingRowValue.position);
      }
      setEditingRowValue({});
      treeGrid.current!.slickGrid.setSelectedRows([]);
      setToolBarBtnState((prev) => ({
        ...prev,
        add_top: canAddWBS,
        add_child: false,
        edit: false,
        delete: false,
        update: false,
        cancel: false,
        loading: {
          update: false,
          cancel: false,
        },
      }));
    };

    const validateRow = (data: EditingWBSRow) => !!(data.code && data.description);

    const saveWBSData = async () => {
      const response =
        editingRowValue.action === "update"
          ? await WBSController({
              action: "update" as const,
              projectId: params.projectId,
              value: {
                _id: editingRowValue._id!,
                code: editingRowValue.code!,
                description: editingRowValue.description!,
              },
            })
          : await WBSController({
              action: "insert" as const,
              // Non-null: this component only renders on the /:projectId project-settings route, so the
              // segment is always present (React Router types every param as possibly-absent). Unlike
              // the update branch, wbsControllerInsertRequest requires projectId — it gates
              // ADD_PROJECT_WBS_ITEMS — so omitting it would 400 rather than silently no-op.
              projectId: params.projectId!,
              value: {
                code: editingRowValue.code!,
                description: editingRowValue.description!,
                parentId: editingRowValue.parentId,
              },
            });
      if (response.data.status) {
        showSuccessToastData(
          editingRowValue._id ? "WBS has been updated successfully" : "WBS has been created successfully",
        );
        treeGrid.current!.refresh!();
        setDeleteConfirmationModal(false);
      } else {
        showErrorToastData(response.data.errorMessage || "Error while saving, please try again");
      }
      cancelEditRecord();
    };

    const toolBarClickHandler = (buttonClicked: string) => {
      const selectedRecords = treeGrid.current!.dataView.getAllSelectedItems();
      const requiredPermission: Record<string, boolean> = {
        add_top: canAddWBS,
        add_child: canAddWBS,
        edit: canEditWBS,
        delete: canDeleteWBS,
        update: editingRowValue.action === "update" ? canEditWBS : canAddWBS,
      };
      if (buttonClicked in requiredPermission && !requiredPermission[buttonClicked]) {
        showErrorToastData(t("You do not have permission to perform this action."));
        return;
      }
      if (buttonClicked == "add_top") {
        const rowToAdd: EditingWBSRow = {
          action: "insert",
          id: 1,
          position: 0,
          unitId: "",
          code: "",
          description: "",
          type: "",
          typeId: null,
          heldBy: null,
          reportsTo: null,
          parentId: null,
          treeLevel: 0,
          hasChild: false,
          __hasChildren: false,
          __used: true,
        };
        treeGrid.current!.addRecord!(rowToAdd, 0);
        setEditingRowValue(rowToAdd);
        setToolBarBtnState((prev) => ({
          ...prev,
          add_top: false,
          add_child: false,
          edit: false,
          delete: false,
          update: true,
          cancel: true,
        }));
      } else if (buttonClicked == "add_child") {
        const currentRecord = selectedRecords[0];
        if (currentRecord?._id) {
          const expandIds = currentRecord?.allParents?.map((id: string) => ({ _id: id, isCollapsed: false })) ?? [];
          treeGrid.current!.treeDataService.dynamicallyToggleItemState(
            expandIds as typeof expandIds &
              Parameters<NonNullable<typeof treeGrid.current>["treeDataService"]["dynamicallyToggleItemState"]>[0],
          );
        }
        const rowToAdd: EditingWBSRow = {
          action: "insert",
          id: 1,
          unitId: "",
          code: "",
          description: "",
          type: "",
          typeId: null,
          heldBy: null,
          reportsTo: null,
          parentId: selectedRecords[0]!._id,
          treeLevel: selectedRecords[0]!.treeLevel! + 1,
          hasChild: false,
          allParents: [selectedRecords[0]!._id!, ...(currentRecord?.allParents ?? [])],
          __hasChildren: false,
          __used: true,
        };
        const position = treeGrid.current!.slickGrid.getSelectedRows()[0];
        rowToAdd.position = position! + 1;
        treeGrid.current!.addRecord!(rowToAdd, rowToAdd.position);
        setEditingRowValue(rowToAdd);
        setToolBarBtnState((prev) => ({
          ...prev,
          add_top: false,
          add_child: false,
          edit: false,
          delete: false,
          update: true,
          cancel: true,
        }));
      } else if (buttonClicked == "edit") {
        setToolBarBtnState((prev) => ({
          ...prev,
          add_top: false,
          add_child: false,
          edit: false,
          delete: false,
          update: true,
          cancel: true,
        }));
        const currentRecord = selectedRecords[0];
        setEditingRowValue({
          ...currentRecord,
          action: "update",
        });
      } else if (buttonClicked == "cancel") {
        cancelEditRecord(editingRowValue.action == "insert");
      } else if (buttonClicked == "delete") {
        setDeleteConfirmationModal(true);
      } else if (buttonClicked == "update") {
        const validation = validateRow(editingRowValue);
        if (!validation) {
          return dispatch(
            showToastNotification({
              content: t("Code and Description are mandatory fields"),
              type: TOAST_TYPE_ERROR,
            }),
          );
        } else {
          setToolBarBtnState((prev: ToolBarBtnState) => ({
            ...prev,
            loading: {
              update: true,
              cancel: true,
            },
          }));
          saveWBSData();
        }
      } else if (buttonClicked === "expandAll") {
        treeGrid.current?.treeDataService.toggleTreeDataCollapse(false);
      } else if (buttonClicked === "collapseAll") {
        treeGrid.current?.treeDataService.toggleTreeDataCollapse(true);
      }
    };

    // Call sites pass plain English literals and server error strings; the toast boundary is where
    // they become translated content.
    const showErrorToastData = (content: string) => {
      dispatch(
        showToastNotification({
          content: content as TranslatedString,
          type: TOAST_TYPE_ERROR,
        }),
      );
    };
    const showSuccessToastData = (content: string) => {
      dispatch(
        showToastNotification({
          content: content as TranslatedString,
          type: TOAST_TYPE_SUCCESS,
        }),
      );
    };

    const deletePopup = () => (
      <div>
        <ModalStateDialogComponent
          allowModalPadding={false}
          footerContent={
            <div className=" flex gap-4">
              <ButtonComponent buttonType={BUTTON_TYPES.BORDER_BUTTON} onClick={handleDeleteModalClose}>
                {t("Cancel")}
              </ButtonComponent>
              <ButtonComponent onClick={handleDeleteWBS} buttonType="gradientButton">
                {t("Confirm")}
              </ButtonComponent>
            </div>
          }
          onCancel={handleDeleteModalClose}
          headerTitle={
            <div className=" w-full flex justify-between rounded-md">
              <div className="">{t("Delete selected WBS")}</div>
            </div>
          }
          isVisible={showDeleteConfirmationModal}
          modelContent={
            <div className="">
              {t("Child WBS (if any) will also be deleted. Are you sure you want to delete the selected WBS?")}
            </div>
          }
          width="30%"
        />
      </div>
    );

    const handleDeleteModalClose = () => {
      setDeleteConfirmationModal(false);
    };

    const handleDeleteWBS = async () => {
      const selectedRow = treeGrid.current!.dataView.getAllSelectedItems();
      const record = selectedRow.map((rowData) => rowData._id!);
      const data = {
        action: "delete",
        record,
      };

      const response = await WBSController(data);
      if (response.data.status) {
        showSuccessToastData("Selected WBS item(s) deleted successfully");
        treeGrid.current!.refresh!();
        setDeleteConfirmationModal(false);
      } else {
        showErrorToastData(response.data.errorMessage || "Error while deleting, please try again");
      }
    };

    const percentChange = (event: { value: number | null }, _id: ObjectIdString, revisedAmount: number) => {
      const value = event.value;
      const amount = (value! * revisedAmount) / 100;

      const numericTextBoxElement = document.getElementById(`${_id}amount`) as (HTMLElement & { value: number }) | null;
      if (numericTextBoxElement) {
        numericTextBoxElement.value = amount ?? 0;
      }
      setCustomValues!((prev) => ({
        ...prev,
        [_id]: {
          amount,
          percent: value!,
        },
      }));
    };
    const amountChange = (event: { value: number | null }, _id: ObjectIdString, revisedAmount: number) => {
      const value = event.value;
      const percent = (value! * 100) / revisedAmount;
      const numericTextBoxElement = document.getElementById(`${_id}percent`) as
        | (HTMLElement & { value: number })
        | null;
      if (numericTextBoxElement) {
        numericTextBoxElement.value = percent ?? 0;
      }
      setCustomValues!((prev) => ({
        ...prev,
        [_id]: {
          amount: value!,
          percent,
        },
      }));
    };
    const percentTemplate = (_row: number, _cell: number, _value: unknown, _columnDef: unknown, args: WBSRow) => {
      const { availableAmount, isAlreadyAddedInThisContract, originalItemId, hasChild, revisedAmount } = args;
      if (hasChild) {
        return;
      }
      const max = (availableAmount! * 100) / revisedAmount!;
      const value = customValues![originalItemId!]?.percent || 0;
      const container = document.createElement("div");
      const root = createRoot(container);
      root.render(
        <div className="flex gap-2 items-center" onClick={(e: React.SyntheticEvent) => e.stopPropagation()}>
          <NumericTextBoxComponent
            id={`${originalItemId}percent`}
            onChange={(event) => percentChange(event, originalItemId!, revisedAmount!)}
            disabled={isAlreadyAddedInThisContract}
            value={value}
            min={0}
            max={max}
          />

          <IconComponent
            name="details"
            width="1.5rem"
            height="1.5rem"
            position="top"
            toolTipContent={t(
              "This percentage of amount will be taken from the available amount of each item linked with this particular Wbs item in the parent contract",
            )}
          />
        </div>,
      );
      return container;
    };
    const availableBoqBudgetTemplate = (
      _row: number,
      _cell: number,
      _value: unknown,
      _columnDef: unknown,
      args: WBSRow,
    ) => {
      const { revisedAmount, boqItemsAmount } = args;
      return revisedAmount! - boqItemsAmount!;
    };
    const linkedAmountTemplate = (_row: number, _cell: number, _value: unknown, _columnDef: unknown, args: WBSRow) => {
      const { availableAmount, isAlreadyAddedInThisContract, originalItemId, hasChild, revisedAmount } = args;
      if (hasChild) {
        return "";
      }
      const value = customValues![originalItemId!]?.amount || 0;
      const container = document.createElement("div");
      const root = createRoot(container);
      root.render(
        <div onClick={(e: React.SyntheticEvent) => e.stopPropagation()}>
          <NumericTextBoxComponent
            id={`${originalItemId}amount`}
            onChange={(event) => amountChange(event, originalItemId!, revisedAmount!)}
            disabled={isAlreadyAddedInThisContract}
            value={value}
            min={0}
            max={availableAmount}
          />
        </div>,
      );
      return container;
    };

    // const dataBound = () => {
    //   if (selectedWBSItems.length) {
    //     treeGrid.current.dataResults?.result?.map((data: any, index: number) => {
    //       if (selectedWBSItems.includes(data.originalItemId)) {
    //         treeGrid.current.selectRow(index);
    //       }
    //     });
    //   }
    // };

    const basicEditTemplate = (field: "code" | "description") => (
      <TextFieldComponent
        onChange={(e) => setEditingRowValue((prev) => ({ ...prev, [field]: e.target.value }))}
        defaultValue={editModeRef?.current?.[field]}
      />
    );

    const initialCols: ColumnDefinition<WBSRow>[] = [
      {
        field: "code",
        id: "code",
        name: t("WBS Code"),
        width: 100,
        // slickgrid-react's own Formatter signature declares a wider `columnDef`/`dataContext` than the
        // wrapper's ColumnDefinition does; the brands are intersected rather than erased through `any`.
        formatter: Formatters.tree as typeof Formatters.tree & NonNullable<ColumnDefinition<WBSRow>["formatter"]>,
        editorTemplate: () => basicEditTemplate("code"),
      },

      {
        field: "description",
        id: "description",
        name: t("WBS Description"),
        width: 120,
        editorTemplate: () => basicEditTemplate("description"),
      },
    ];

    const [columnDefinitions, setColumnDefinitions] = useState<ColumnDefinition<WBSRow>[]>(initialCols);

    useEffect(() => {
      const cols = [...initialCols];
      if (showColumnForParentWbs) {
        cols.push(
          {
            field: "availableAmount",
            id: "availableAmount",
            name: t("Available Amount"),
            width: 120,
          },
          {
            field: "linkedAmount",
            id: "linkedAmount",
            formatter: linkedAmountTemplate,
            name: t("linked Amount"),
            width: 120,
          },
          {
            field: "availableAmount",
            id: "availableAmount",
            formatter: percentTemplate,
            name: t("Percent(%)"),
            width: 120,
          },
        );
      }
      if (showBoqBudgetColumn) {
        cols.push({
          field: "revisedAmount",
          id: "revisedAmount",
          name: t("Total Budget"),
          width: 120,
        });
      }
      if (showAvailableBudgetColumn) {
        cols.push({
          field: "availableAmount",
          id: "availableAmount",
          formatter: availableBoqBudgetTemplate,
          name: t("Available Budget"),
          width: 120,
        });
      }
      cols.push(
        {
          field: renameHeaders ? "addedOn" : "createdDate",
          id: renameHeaders ? "addedOn" : "createdDate",
          name: renameHeaders ? t("Added On") : t("Created On"),
          width: 120,
          formatter: dateTemplate,
          type: "date",
        },

        {
          field: "fullName",
          id: "fullName",
          name: renameHeaders ? t("Added By") : t("Created by"),
          width: 120,
          _valueGetter: (row) => (renameHeaders ? row.addedBy : row.createdBy),
          formatter: addedByTemplate,
        },
      );
      setColumnDefinitions(cols);
    }, []);

    const beforeDataBound = (data: WBSRemoteResponse) => {
      const disabledItemIds = data?.data?.result
        ?.filter((item) => item.isAlreadyAddedInThisForm || item.isAlreadyAddedInThisContract)
        .map((item) => item?.[idMapping as keyof WBSRow] as ObjectIdString);
      setDisabledRows((prev) => [...prev, ...disabledItemIds!]);
      return data.data!.result!;
    };

    const treeGridData = () => {
      return (
        <SlickGrid
          ref={treeGrid}
          columnDefinitions={columnDefinitions as ValidatedColumnDefinition<WBSRow>[]}
          treeGrid
          disableCustomSorting
          treeColIndex={1}
          enableSelectAllCheckbox
          initialSortColumnId="createdDate"
          beforeDataBound={beforeDataBound as (resp: unknown) => WBSRow[]}
          initialSortDirection="ASC"
          disabledIds={disabledRows}
          allowPagination={false}
          idMapping={idMapping}
          disableChildDataFetch
          hasChildMapping="hasChild"
          enableWrapText={isWrapTextActive}
          setEnableWrapText={setIsWrapTextActive}
          parentIdMapping="parentId"
          onSelectedRowIdsChanged={rowSelectedHandler}
          toolbar={toolbarOptions}
          disableTreeCollapseOnDataChange
          isEditMode={!!editingRowValue.action}
          toolbarClickHandler={toolBarClickHandler}
          remoteDataSource={dataSourceUrl}
          query={dataSourceQuery}
          enableFiltering={true}
          enableFilteringOption
          enableSortingOption
          enableGroupingOption
          enableColumnChooser
        />
      );
    };

    return (
      <div className="flex w-full h-full flex-col overflow-auto">
        {showDeleteConfirmationModal && deletePopup()}
        <GridPageLayout
          showHeader={!showOnlyGrid}
          allowOuterPadding={false}
          title={t("WBS")}
          description={t("Work Breakdown Structure")}
        >
          {_.get(getProjectSettingObject, "WBS.isVisible", false) ? <>{treeGridData()}</> : <Forbidden />}
        </GridPageLayout>
      </div>
    );
  },
);
ProjectWBS.displayName = "ProjectWBS";
export default ProjectWBS;
